"use client";

import { ProjectMemberRole } from "@prisma/client";
import { useRouter } from "next/navigation";
import { Components } from "@/app/(components)/_components/Components";
import ActionMenu from "@/app/(components)/generics/ActionMenu/ActionMenu";
import EditableText from "@/app/(components)/generics/EditableText/EditableText";
import IconTrash from "@/app/(components)/generics/Icons/IconTrash";
import TabbedContent from "@/app/(components)/generics/TabbedContent/TabbedContent";
import Members from "@/app/(components)/specifics/projet/Project/tabs/Members/Members";
import Overview from "@/app/(components)/specifics/projet/Project/tabs/Overview/Overview";
import {
  useBrand,
  useCanManageCompany,
} from "@/app/(components)/specifics/wrappers/BrandProvider/BrandProvider";
import PageWrapper from "@/app/(components)/specifics/wrappers/PageWrapper/PageWrapper";
import { updateProject } from "@/app/actions/project/update/action";
import { useFetchProjectWorkspace } from "@/app/hooks/fetchs/project/useFetchProjectWorkspace";
import { useFetchProjectMembers } from "@/app/hooks/fetchs/project-member/useFetchProjectMembers";
import { ModalStore } from "@/app/stores/ModalStore";
import { ProjectStore } from "@/app/stores/ProjectStore";
import { UserStore } from "@/app/stores/UserStore";
import { TKey } from "@/app/translations/TKey";
import { useT } from "@/app/translations/useT";
import { DefaultProps } from "@/app/types/common/DefaultProps";
import { PageBootstrap } from "@/app/types/common/WorkspaceSelection";
import { ProjectLite } from "@/app/types/project/ProjectLite";
import { ProjectWorkspace } from "@/app/types/project/ProjectWorkspace";
import { ULogger } from "@/app/utils/ULogger";
import { UProjectMember } from "@/app/utils/UProjectMember";
import { URLs } from "@/app/utils/URLs";
import { Limits } from "@/app/vars/Limits";
import { Routes } from "@/app/vars/Routes";
import { UrlParam } from "@/app/vars/Urls";
import { ProjectTab } from "./Project.logic";
import styles from "./Project.module.css";

type Props = DefaultProps & {
  project: ProjectLite;
  bootstrap: PageBootstrap<ProjectWorkspace> | null;
};

export default function Project(props: Props) {
  const T = useT();
  const { params, bootstrap } = props;
  const { lang } = params;
  const router = useRouter();

  const storedProject = ProjectStore((state) => state.currentProject);
  const project = storedProject?.id === props.project.id ? storedProject : props.project;

  const initialWorkspace = bootstrap?.projectId === project.id ? bootstrap?.data : undefined;

  const { isSuccess: workspaceReady } = useFetchProjectWorkspace({
    projectId: project.id,
    enabled: !!project.id,
    initialData: initialWorkspace,
  });

  const { data: members = [], isLoading: membersLoading } = useFetchProjectMembers({
    projectId: project.id,
    enabled: !!project.id && workspaceReady,
  });

  const handleSaveName = async (name: string) => {
    try {
      const updated = await updateProject(project.id, { name });
      if (updated) ProjectStore.getState().updateProject(updated);
    } catch (err) {
      ULogger.error("Failed to save project name", err);
    }
  };

  const isTenant = useBrand() !== null;
  const canManageCompany = useCanManageCompany();

  const projects = ProjectStore((state) => state.projects);
  const currentUserId = UserStore((state) => state.user?.id);
  const myRole = UProjectMember.getRole(members, currentUserId);
  const isOwner = myRole === ProjectMemberRole.OWNER;
  // On a company subdomain the roster stores everyone as MEMBER, so managers are
  // authoritative via canManageCompany; on the main domain we read the roster role.
  const canEditProject = isTenant
    ? canManageCompany
    : myRole === ProjectMemberRole.OWNER || myRole === ProjectMemberRole.ADMIN;
  // The members roster is a company-only surface: personal accounts have no members tab.
  const canManageMembers = isTenant && canManageCompany;
  const isLastProject = projects.length <= 1;

  const handleDeleteClick = () => {
    if (isLastProject) return;
    ModalStore.getState().openModal(Components.ProjectDeletion, T(TKey.PROJECT_DELETE) as string, {
      lang,
      project,
      onDeleted: () => {
        ProjectStore.getState().removeProject(project.id);
        router.push(URLs.pathOf(Routes.HOME));
      },
    });
  };

  const items = [
    {
      id: ProjectTab.OVERVIEW,
      title: T(TKey.PROJECT_TAB_OVERVIEW) as string,
      content: <Overview params={params} project={project} enabled={workspaceReady} />,
    },
    ...(canManageMembers
      ? [
          {
            id: ProjectTab.MEMBERS,
            title: T(TKey.PROJECT_TAB_EXTERNAL_MEMBERS) as string,
            content: (
              <Members
                params={params}
                projectId={project.id}
                members={members}
                isLoading={membersLoading}
              />
            ),
          },
        ]
      : []),
  ];

  return (
    <PageWrapper
      title={
        <EditableText
          value={project.name}
          maxLength={Limits.MAX_PROJECT_NAME}
          onSave={handleSaveName}
          placeholder={T(TKey.PROJECT) as string}
          required
          alignCenter
          disabled={!canEditProject}
          textClassName={styles.titleText}
          inputClassName={styles.titleInput}
        />
      }
      rightElement={
        <ActionMenu
          items={[
            isOwner &&
              !isLastProject && {
                icon: <IconTrash />,
                label: T(TKey.DELETE) as string,
                onClick: handleDeleteClick,
                danger: true,
              },
          ]}
        />
      }
    >
      <div className={styles.container}>
        {items.length > 1 ? (
          <TabbedContent
            items={items}
            initialItemId={ProjectTab.OVERVIEW}
            urlParamName={UrlParam.TAB}
          />
        ) : (
          items[0].content
        )}
      </div>
    </PageWrapper>
  );
}
